import { useState, useCallback, useEffect } from "react";
import { Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import LargePagination from "../core/LargePagination";
import { config } from "../../config";
import { userService } from "../../services/UserService";
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    EditButton,
    DeleteButton,
    Edit,
    SimpleForm,
    TextInput,
    Labeled,
    Create,
    CreateButton,
    ReferenceInput,
    ReferenceField,
    SelectInput,
    Toolbar,
    SaveButton,
    TopToolbar,
    useNotify,
    usePermissions,
    useRecordContext,
    useRedirect,
    required,
} from "react-admin";

const hasReadAccess = (permissions) =>
    permissions?.some((p) => p === "on_prem_files.read" || p === "on_prem_files.read_all");

const hasWriteAccess = (permissions) =>
    permissions?.includes("on_prem_files.write");

const usePermissionGuard = (checkFn) => {
    const { permissions, isLoading } = usePermissions();
    const redirect = useRedirect();
    useEffect(() => {
        if (!isLoading && !checkFn(permissions)) {
            redirect("/");
        }
    }, [permissions, isLoading, redirect]);
    return { blocked: isLoading || !checkFn(permissions), permissions };
};

const DownloadButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const [loading, setLoading] = useState(false);

    const handleDownload = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${config.apiUrl}/onPremFiles/${record.onPremFileId}/presignedUrl`, {
                headers: { Authorization: userService.getAuthToken() },
            });
            if (!res.ok) {
                notify("Failed to generate download link", { type: "error" });
                return;
            }
            const { presignedUrl } = await res.json();
            window.open(presignedUrl, "_blank", "noopener,noreferrer");
        } catch (e) {
            notify("An error occurred while generating the download link", { type: "error" });
        } finally {
            setLoading(false);
        }
    }, [record, notify]);

    return (
        <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={(e) => { e.stopPropagation(); handleDownload(); }}
            disabled={loading}
        >
            Download
        </Button>
    );
};

const onPremFileFilters = [
    <ReferenceInput key="clientId" source="clientId" reference="portal/clients" label="Client" alwaysOn>
        <SelectInput optionText={(r) => `${r.clientName} (id: ${r.clientId})`} label="Client" />
    </ReferenceInput>,
];

export const OnPremFileList = () => {
    const { blocked, permissions } = usePermissionGuard(hasReadAccess);
    if (blocked) return null;
    const canWrite = hasWriteAccess(permissions);
    return (
        <List
            filters={onPremFileFilters}
            sort={{ field: "clientName", order: "ASC" }}
            disableSyncWithLocation
            pagination={<LargePagination />}
            bulkActionButtons={false}
            exporter={false}
            actions={<TopToolbar>{canWrite && <CreateButton />}</TopToolbar>}
        >
            <Datagrid bulkActionButtons={false}>
                <TextField source="onPremFileId" label="ID" sortable={false} />
                <ReferenceField source="clientId" reference="portal/clients" label="Client" link={false} sortable={false}>
                    <FunctionField render={(record) => `${record.clientName} (id: ${record.clientId})`} />
                </ReferenceField>
                <TextField source="name" label="File Name" sortable={false} />
                <TextField source="sha256" label="SHA-256" sortable={false} />
                <TextField source="s3ObjectKey" label="S3 Object Key" sortable={false} />
                <DateField source="createDttm" label="Date Uploaded" showTime sortable={false} />
                <DownloadButton label="Download" />
                {canWrite && <EditButton />}
                {canWrite && (
                    <DeleteButton
                        mutationMode="pessimistic"
                        confirmTitle="Delete On-Prem File"
                        confirmContent="Are you sure you want to delete this file? This cannot be undone."
                    />
                )}
            </Datagrid>
        </List>
    );
};


const OnPremFileEditToolbar = () => {
    const redirect = useRedirect();
    return (
        <Toolbar>
            <SaveButton />
            <Button onClick={() => redirect("list", "portal/onPremFiles")} style={{ marginLeft: 8 }}>
                Back
            </Button>
        </Toolbar>
    );
};

export const OnPremFileEdit = () => {
    const { blocked } = usePermissionGuard(hasWriteAccess);
    const notify = useNotify();
    if (blocked) return null;

    const handleSaveError = (error) => {
        if (error.status === 409) {
            notify(error.body?.message || "A file with this name already exists for this client.", { type: "error" });
        } else {
            notify("An error occurred while saving.", { type: "error" });
        }
    };

    return (
        <Edit title="Edit On-Prem File" mutationOptions={{ onError: handleSaveError }}>
            <SimpleForm toolbar={<OnPremFileEditToolbar />}>
                <Labeled label="ID"><TextField source="onPremFileId" /></Labeled>
                <Labeled label="Client">
                    <ReferenceField source="clientId" reference="portal/clients" link={false}>
                        <FunctionField render={(record) => `${record.clientName} (id: ${record.clientId})`} />
                    </ReferenceField>
                </Labeled>
                <TextInput source="name" label="File Name" validate={required()} />
                <Labeled label="SHA-256"><TextField source="sha256" /></Labeled>
                <Labeled label="S3 Object Key"><TextField source="s3ObjectKey" /></Labeled>
                <Labeled label="Date Uploaded"><DateField source="createDttm" showTime /></Labeled>
                <Labeled label="Last Updated"><DateField source="lastUpdatedDttm" showTime /></Labeled>
            </SimpleForm>
        </Edit>
    );
};

const hexToBase64 = (hex) => {
    const bytes = hex.match(/.{2}/g).map((h) => String.fromCharCode(parseInt(h, 16)));
    return btoa(bytes.join(""));
};

const computeSha256 = async (file) => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

export const OnPremFileCreate = () => {
    const { blocked } = usePermissionGuard(hasWriteAccess);
    const [file, setFile] = useState(null);
    const [sha256, setSha256] = useState(null);
    const [hashing, setHashing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const notify = useNotify();
    const redirect = useRedirect();
    if (blocked) return null;

    const handleFileChange = async (e) => {
        const selected = e.target.files[0] || null;
        setFile(selected);
        setSha256(null);
        if (selected) {
            setHashing(true);
            try {
                const hash = await computeSha256(selected);
                setSha256(hash);
            } catch (err) {
                console.error("Failed to compute SHA-256:", err);
                notify("Failed to compute file hash", { type: "error" });
            } finally {
                setHashing(false);
            }
        }
    };

    const handleSubmit = async (values) => {
        if (!file) {
            notify("Please select a file to upload", { type: "error" });
            return;
        }
        if (!sha256) {
            notify("File hash is still being computed, please wait", { type: "error" });
            return;
        }

        setUploading(true);
        try {
            // Step 1: Get presigned upload URL from API
            const urlRes = await fetch(`${config.apiUrl}/onPremFiles/presignedUploadUrl`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: userService.getAuthToken(),
                },
                body: JSON.stringify({
                    clientId: values.clientId,
                    name: values.name,
                    fileName: file.name,
                    sha256,
                    contentType: file.type || "application/octet-stream",
                }),
            });

            if (!urlRes.ok) {
                notify("Failed to initiate upload", { type: "error" });
                return;
            }

            const { presignedUrl, s3ObjectKey } = await urlRes.json();

            // Step 2: Upload file directly to S3 with the checksum header.
            // S3 will reject the upload if the file content does not match the computed SHA-256.
            const s3Res = await fetch(presignedUrl, {
                method: "PUT",
                headers: {
                    "Content-Type": file.type || "application/octet-stream",
                    "x-amz-checksum-sha256": hexToBase64(sha256),
                },
                body: file,
            });

            if (!s3Res.ok) {
                notify("Upload rejected by S3. Please try again or contact engineering.", { type: "error" });
                return;
            }

            // Step 3: Create the database record now that the file is confirmed in S3
            const createRes = await fetch(`${config.apiUrl}/onPremFiles`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: userService.getAuthToken(),
                },
                body: JSON.stringify({
                    clientId: values.clientId,
                    name: values.name,
                    sha256,
                    s3ObjectKey,
                }),
            });

            if (!createRes.ok) {
                if (createRes.status === 409) {
                    const body = await createRes.json().catch(() => ({}));
                    notify(body.message || "A file with this name already exists for this client.", { type: "error" });
                } else {
                    notify("File uploaded to S3 but the database record could not be saved. Contact engineering.", { type: "error" });
                }
                return;
            }

            notify("File uploaded successfully", { type: "success" });
            redirect("list", "portal/onPremFiles");
        } catch (e) {
            console.error("On-prem file upload error:", e);
            notify("An unexpected error occurred during upload", { type: "error" });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Create title="Upload On-Prem File">
            <SimpleForm onSubmit={handleSubmit}>
                <ReferenceInput source="clientId" reference="portal/clients">
                    <SelectInput optionText={(r) => `${r.clientName} (id: ${r.clientId})`} validate={required()} />
                </ReferenceInput>
                <TextInput source="name" label="File Name" validate={required()} />
                <input
                    type="file"
                    disabled={uploading || hashing}
                    onChange={handleFileChange}
                    style={{ margin: "8px 0 4px" }}
                />
                {hashing && (
                    <p style={{ margin: "0 0 16px", color: "#666" }}>Computing SHA-256 hash...</p>
                )}
                {sha256 && (
                    <p
                        data-testid="sha256-display"
                        style={{ margin: "0 0 16px", wordBreak: "break-all", color: "#444" }}
                    >
                        SHA-256: {sha256}
                    </p>
                )}
            </SimpleForm>
        </Create>
    );
};
